const API_BASE = '/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class ApiError extends Error {
  status: number;
  
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });
  
  const data: ApiResponse<T> = await response.json();
  
  if (!response.ok || !data.success) {
    throw new ApiError(
      data.error || data.message || 'An error occurred',
      response.status
    );
  }
  
  return data.data as T;
}

// Auth API
export const authApi = {
  login: (email: string, password: string, rememberMe: boolean = false) =>
    fetchApi<{ user: any; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, rememberMe }),
    }),
  
  register: (data: { email: string; password: string; name: string; phone?: string }) =>
    fetchApi<{ user: any; token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  logout: () =>
    fetchApi<void>('/auth/logout', { method: 'POST' }),
  me: async () => {
    const res = await fetch(API_BASE + "/auth/me", { credentials: "include" });
    if (!res.ok) return null;
    const data = await res.json();
    return data.data;
  },
  
  forgotPassword: (email: string) =>
    fetchApi<void>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  
  resetPassword: (token: string, password: string) =>
    fetchApi<void>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
  updateProfile: (data: { name?: string; phone?: string; avatarUrl?: string }) =>
    fetchApi<{ user: any }>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    fetchApi<void>('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// Courses API
export const coursesApi = {
  getById: (id: string) => fetchApi(`/courses/by-id/${id}`),
  list: (params?: { language?: string; level?: string; featured?: boolean }) => {
    const query = new URLSearchParams();
    if (params?.language) query.set('language', params.language);
    if (params?.level) query.set('level', params.level);
    if (params?.featured) query.set('featured', 'true');
    const queryString = query.toString();
    return fetchApi<any[]>(`/courses${queryString ? `?${queryString}` : ''}`);
  },
  
  get: (slug: string) =>
    fetchApi<any>(`/courses/${slug}`),
  
  getCurriculum: (courseId: string) =>
    fetchApi<any>(`/courses/${courseId}/curriculum`),
};

// Student API
export const studentApi = {
  getDashboard: () =>
    fetchApi<any>('/student/dashboard'),
  
  getCourse: (courseId: string) =>
    fetchApi<any>(`/student/courses/${courseId}`),
  
  getProfile: () =>
    fetchApi<any>('/student/profile'),
  
  updateProfile: (data: { name?: string; phone?: string }) =>
    fetchApi<any>('/student/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// Videos API
export const videosApi = {
  getStreamUrl: (lessonId: string) =>
    fetchApi<{ playlistUrl: string; expiresAt: string; watermark: any; duration: number }>(
      `/videos/${lessonId}/stream`
    ),
  getFreeStreamUrl: (lessonId: string) =>
    fetchApi<{ playlistUrl: string }>(
      `/videos/${lessonId}/free-stream`
    ),
  
  updateProgress: (lessonId: string, data: { watchedSeconds?: number; lastPosition?: number; isCompleted?: boolean }) =>
    fetchApi<{ progressPercent: number; isCompleted: boolean }>(
      `/videos/${lessonId}/progress`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    ),
};

// Payments API
export const paymentsApi = {
  initiate: (courseId: string, locale: "en" | "ar" = "en", couponCode?: string) =>
    fetchApi<{ orderId: string; redirectUrl: string }>("/payments/initiate", {
      method: "POST",
      body: JSON.stringify({ courseId, locale, couponCode }),
    }),
  validateCoupon: (code: string, courseId: string) =>
    fetchApi<{ couponId: string; code: string; discountType: string; discountValue: number; discount: number; originalPrice: number; finalPrice: number }>("/payments/validate-coupon", {
      method: "POST",
      body: JSON.stringify({ code, courseId }),
    }),
  
  verify: (orderId: string, transactionId?: string) =>
    fetchApi<{ status: string; enrollment?: any; error?: string }>('/payments/verify', {
      method: 'POST',
      body: JSON.stringify({ orderId, transactionId }),
    }),
  
  getOrders: () =>
    fetchApi<any[]>('/payments/orders'),
  
  mockComplete: (orderId: string) =>
    fetchApi<{ status: string; enrollment?: any }>('/payments/mock-complete', {
      method: 'POST',
      body: JSON.stringify({ orderId }),
    }),
};

// Contact API
export const contactApi = {
  submit: (data: { name: string; email: string; phone?: string; subject: string; message: string }) =>
    fetchApi<void>('/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Admin API
export const adminApi = {
  getDashboard: () =>
    fetchApi<any>('/admin/dashboard'),
  
  getCourses: () =>
    fetchApi<any>('/admin/courses'),
  getCourse: (id: string) =>

    fetchApi<any>(`/admin/courses/${id}`),
  
  createCourse: (data: any) =>
    fetchApi<any>('/admin/courses', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateCourse: (id: string, data: any) =>
    fetchApi<any>(`/admin/courses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  deleteCourse: (id: string) =>
    fetchApi<void>(`/admin/courses/${id}`, { method: 'DELETE' }),
  
  getModules: (courseId: string) =>
    fetchApi<any[]>(`/admin/courses/${courseId}/modules`),
  
  createModule: (data: any) =>
    fetchApi<any>('/admin/modules', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateModule: (id: string, data: any) =>
    fetchApi<any>(`/admin/modules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  deleteModule: (id: string) =>
    fetchApi<void>(`/admin/modules/${id}`, { method: 'DELETE' }),
  
  createLesson: (data: any) =>
    fetchApi<any>('/admin/lessons', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateLesson: (id: string, data: any) =>
    fetchApi<any>(`/admin/lessons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  deleteLesson: (id: string) =>
    fetchApi<void>(`/admin/lessons/${id}`, { method: 'DELETE' }),
  
  getUploadUrl: (lessonId: string, contentType: string, filename: string) =>
    fetchApi<{ uploadUrl: string; videoKey: string }>(`/admin/lessons/${lessonId}/upload-url`, {
      method: 'POST',
      body: JSON.stringify({ contentType, filename }),
    }),
  
  getFileUploadUrl: (lessonId: string, contentType: string, filename: string) =>
    fetchApi<{ uploadUrl: string; fileKey: string }>(`/admin/lessons/${lessonId}/file-upload-url`, {
      method: 'POST',
      body: JSON.stringify({ contentType, filename }),
    }),
  
  startTranscode: (lessonId: string, videoKey: string, duration: number) =>
    fetchApi<any>(`/admin/lessons/${lessonId}/transcode`, {
      method: 'POST',
      body: JSON.stringify({ videoKey, duration }),
    }),
  
  getTranscodeStatus: (jobId: string) =>
    fetchApi<any>(`/admin/transcode/${jobId}`),
  
  getUsers: (params?: { role?: string; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.role) query.set('role', params.role);
    if (params?.search) query.set('search', params.search);
    const queryString = query.toString();
    return fetchApi<any[]>(`/admin/users${queryString ? `?${queryString}` : ''}`);
  },
  updateUserRole: (id: string, role: string) =>
    fetchApi<void>(`/admin/users/${id}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),
  
  deleteUser: (id: string) =>
    fetchApi<void>(`/admin/users/${id}`, {
      method: 'DELETE',
    }),
  
  getEnrollments: () =>
    fetchApi<any[]>('/admin/enrollments'),
  createEnrollment: (userId: string, courseId: string) =>
    fetchApi<any>("/admin/enrollments", {
      method: "POST",
      body: JSON.stringify({ userId, courseId }),
    }),
  deleteEnrollment: (id: string) =>
    fetchApi<void>(`/admin/enrollments/${id}`, { method: "DELETE" }),
  getCoupons: () =>
    fetchApi<any[]>("/admin/coupons"),
  createCoupon: (data: any) =>
    fetchApi<any>("/admin/coupons", { method: "POST", body: JSON.stringify(data) }),
  updateCoupon: (id: string, data: any) =>
    fetchApi<any>(`/admin/coupons/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteCoupon: (id: string) =>
    fetchApi<void>(`/admin/coupons/${id}`, { method: "DELETE" }),
  
  getOrders: () =>
    fetchApi<any[]>('/admin/orders'),
  
  getAccessLogs: (params?: { action?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.action) query.set('action', params.action);
    if (params?.limit) query.set('limit', params.limit.toString());
    const queryString = query.toString();
    return fetchApi<any[]>(`/admin/access-logs${queryString ? `?${queryString}` : ''}`);
  },
  
  getMessages: () =>
    fetchApi<any[]>('/admin/messages'),
  
  markMessageRead: (id: string) =>
    fetchApi<void>(`/admin/messages/${id}/read`, { method: 'PUT' }),

  // Invoices
  getInvoices: (params?: { type?: string; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.type) query.set('type', params.type);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return fetchApi<any[]>('/admin/invoices' + (qs ? '?' + qs : ''));
  },
  getInvoice: (id: string) =>
    fetchApi<any>('/admin/invoices/' + id),
  createInvoice: (data: any) =>
    fetchApi<any>('/admin/invoices', { method: 'POST', body: JSON.stringify(data) }),
  emailInvoice: (id: string) =>
    fetchApi<any>('/admin/invoices/' + id + '/email', { method: 'POST' }),
};

export { ApiError };

// Quiz API (Admin)
export const quizApi = {
  getQuestions: (lessonId: string) =>
    fetchApi<any[]>(`/admin/lessons/${lessonId}/questions`),

  createQuestion: (lessonId: string, data: any) =>
    fetchApi<any>(`/admin/lessons/${lessonId}/questions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateQuestion: (questionId: string, data: any) =>
    fetchApi<any>(`/admin/questions/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteQuestion: (questionId: string) =>
    fetchApi<void>(`/admin/questions/${questionId}`, { method: 'DELETE' }),

  createAnswer: (questionId: string, data: any) =>
    fetchApi<any>(`/admin/questions/${questionId}/answers`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateAnswer: (answerId: string, data: any) =>
    fetchApi<any>(`/admin/answers/${answerId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteAnswer: (answerId: string) =>
    fetchApi<void>(`/admin/answers/${answerId}`, { method: 'DELETE' }),
};

// Student Quiz API
export const studentQuizApi = {
  getQuiz: (lessonId: string) =>
    fetchApi<any>(`/student/lessons/${lessonId}/quiz`),

  submitQuiz: (lessonId: string, answers: Record<string, string>) =>
    fetchApi<any>(`/student/lessons/${lessonId}/quiz`, {
      method: 'POST',
      body: JSON.stringify({ answers }),
    }),
};

// Certificate API
export const certificateApi = {
  getMyCertificates: () =>
    fetchApi<any[]>('/student/certificates'),

  getCertificate: (courseId: string) =>
    fetchApi<any>(`/student/certificates/${courseId}`),

  downloadCertificate: (certificateId: string) =>
    fetchApi<{ downloadUrl: string }>(`/student/certificates/${certificateId}/download`),

  verifyCertificate: (code: string) =>
    fetchApi<any>(`/certificates/verify/${code}`),
};

// Exam Request API (Student)
export const examApi = {
  checkEligibility: (courseId: string) =>
    fetchApi<{ eligible: boolean; reason?: string }>(`/student/exams/${courseId}/eligibility`),

  submitRequest: (courseId: string, data: any) =>
    fetchApi<any>(`/student/exams/${courseId}/request`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  uploadPassport: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetch(`${API_BASE}/student/exams/upload-passport`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    }).then(res => res.json());
  },

  getMyRequests: () =>
    fetchApi<any[]>('/student/exams/requests'),

  getRequest: (requestId: string) =>
    fetchApi<any>(`/student/exams/requests/${requestId}`),
};

// Admin Exam Management API
export const adminExamApi = {
  getRequests: (params?: { status?: string; examType?: string }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.examType) query.set('examType', params.examType);
    const queryString = query.toString();
    return fetchApi<any[]>(`/admin/exams${queryString ? `?${queryString}` : ''}`);
  },

  getRequest: (requestId: string) =>
    fetchApi<any>(`/admin/exams/${requestId}`),

  approveRequest: (requestId: string, data?: { scheduledDate?: string; scheduledTime?: string; notes?: string }) =>
    fetchApi<any>(`/admin/exams/${requestId}/approve`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    }),

  rejectRequest: (requestId: string, reason: string) =>
    fetchApi<any>(`/admin/exams/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  updateStatus: (requestId: string, status: string, notes?: string) =>
    fetchApi<any>(`/admin/exams/${requestId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, notes }),
    }),
};

// Progress API
export const progressApi = {
  getCourseProgress: (courseId: string) =>
    fetchApi<any>(`/student/courses/${courseId}/progress`),

  markLessonComplete: (lessonId: string) =>
    fetchApi<any>(`/student/lessons/${lessonId}/complete`, { method: 'POST' }),

  updateVideoPosition: (lessonId: string, position: number) =>
    fetchApi<any>(`/student/lessons/${lessonId}/position`, {
      method: 'POST',
      body: JSON.stringify({ position }),
    }),

  getLastPosition: (courseId: string) =>
    fetchApi<{ lessonId: string; position: number } | null>(`/student/courses/${courseId}/resume`),
};

// Feature Flags API
export const featureFlagsApi = {
  getFlags: () => fetchApi<Record<string, boolean>>('/feature-flags'),
  getAdminFlags: () => fetchApi<any[]>('/admin/feature-flags'),
  createFlag: (data: { name: string; description?: string; allowedEmails?: string[] }) =>
    fetchApi<any>('/admin/feature-flags', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateFlag: (id: number, data: any) =>
    fetchApi<any>(`/admin/feature-flags/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteFlag: (id: number) =>
    fetchApi<any>(`/admin/feature-flags/${id}`, {
      method: 'DELETE',
    }),
};
